import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import {
    Dataset,
    DatasetFileTypeOption,
    DatasetFiles,
    DatasetFolder,
    FileAction,
    FileColumnKey,
    FileModel,
    ItemsToBeRemovedOptions,
    ListSearch,
    MEDFileColumnKeys,
    MessagingCallbackAction,
    OptionItem,
    SortOption,
    WorkflowType,
} from '@ca/core/models';
import { CziValidatorService, Message, MessagingService, TrackingService, UserService, WorkflowRunQueueService } from '@ca/core/services';
import { getCurrentPageOfFiles, getShownFilesBySearchAndSortOptions } from '@ca/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Guid } from 'guid-typescript';
import { map, tap } from 'rxjs/operators';
import { FileConversionStatus } from 'src/app/core/models/file-conversion-status.model';
import { ArivisService } from 'src/app/core/services/arivis.service';
import { CziConverterService } from 'src/app/core/services/czi-converter.service';
import { MachineLearningService } from 'src/app/core/services/machine-learning.service';
import { FileExplorerMode } from 'src/app/core/types/file-explorer-mode.type';
import * as FileUtils from '../../../../utils/file/file-utils';
import { FILE_EXPLORER_MODE } from '../../constants/constants';
import {
    CZI_CONVERSION_DONE_NEXT_ADD_IMAGES_TO_DATASET,
    CZI_CONVERSION_DONE_NEXT_ANNOTATE_DATASET,
    CZI_CONVERSION_DONE_NEXT_START_TRAINING_WITH_DATASET,
    CZI_CONVERSION_REMOVED_FAILED_CONVERSION_FILE,
    CZI_CONVERSION_RETRY,
} from '../../constants/czi-conversion-messages';

interface FileModelRow extends FileModel {
    selected?: boolean;
}
@UntilDestroy()
@Component({
    selector: 'ca-dataset-explorer',
    templateUrl: './dataset-explorer.component.html',
    styleUrls: ['./dataset-explorer.component.scss'],
})
export class DatasetExplorerComponent implements OnInit, OnDestroy {
    @Input() isLeavingDatasetAllowed = true;
    @Input() isLeavingTheFolderAllowed = true;
    @Input() isDownloadAnnotationsTrackingEventEnabled: boolean = false;
    @Input() set dataset(dataset: Dataset) {
        // please do not change the dataset that comes in else the dataset in the inputting component would also be changed
        this.currentDataset = Dataset.createInstance(structuredClone(dataset));
    }
    @Input() set datasetFiles(datasetFiles: DatasetFiles | undefined) {
        if (datasetFiles) {
            const shouldConvertNotCziFiles = this._datasetFiles != null;

            this.refreshDatasetFiles(datasetFiles);
            if (this.currentFolder != null) {
                this.openFolder(this.currentFolder.folderType, shouldConvertNotCziFiles);
            }

            this.invalidFiles = datasetFiles ? datasetFiles.files.filter(file => FileUtils.hasInvalidValidation(file.validationStatus)) : [];

            this.failedConversionFiles = datasetFiles ? datasetFiles.files.filter(file => FileUtils.hasFailedConversion(file.convertStatus)) : [];

            this.unknownFiles = datasetFiles
                ? datasetFiles.files.filter(file => file.validationStatus && FileUtils.hasUnknownValidationStatus(file.validationStatus))
                : [];

            // Revalidation should only be possible if the files' validationStatus is Pending, Unknown or null  at the start
            if (!this.arePendingFilesSetTheFirstTime) {
                this.arePendingFilesSetTheFirstTime = true;
                this.pendingFilesToValidate = datasetFiles
                    ? datasetFiles.files.filter(file => file.validationStatus && FileUtils.hasPendingValidation(file.validationStatus))
                    : [];
            }
        }
    }

    @Input() isButtonBarShown = true;
    @Input() scrollableItems: boolean;
    /**
     * If needed to initialize dataset explorer in a certain folder.
     */
    @Input() initializeInFolder?: DatasetFileTypeOption.ANNOTATIONS | DatasetFileTypeOption.FILES;
    @Input() fileColumns: FileColumnKey[] | MEDFileColumnKeys[] = [];
    @Input() fileActions: FileAction[] = [];
    /**
     * don't want to change our url (query parameter),
     * when updateParameterQuery set to false
     */
    @Input() updateParameterQuery: boolean = false;
    /**
     * Pass a string here to prepend it before the actual breadcrumb.
     * Used when the Dataset Explorer is embedded into a screen (i.e. in ApeerFileExplorer).
     * A click on this string will trigger the event clickOnPrependedBreadcrumbElement.
     */
    @Input() prependToBreadcrumb?: string;
    /**
     * Shows a select all files button in the files table
     */
    @Input() showSelectAllFilesButton = false;
    @Input() useRouting = true;
    @Input() filesTableMode: FileExplorerMode = FILE_EXPLORER_MODE.MODIFICATION;
    @Input() showFileStatus = true;
    @Input() operationId: string;
    @Input() isUploadModal?: boolean = false;
    @Input() isFilesStatusShown?: boolean = true;

    @Output() leaveDataset = new EventEmitter<void>();
    @Output() annotateDataset = new EventEmitter<Dataset>();
    @Output() addFilesToDataset = new EventEmitter<{ dataset: Dataset; datasetFileTypeOption: DatasetFileTypeOption }>();
    @Output() removeFilesFromDataset = new EventEmitter<{
        datasetFileTypeOption: DatasetFileTypeOption;
        items: FileModel[];
        shouldConfirmDialogBeOpened: boolean;
    }>();
    @Output() clickOnPrependedBreadcrumbElement = new EventEmitter<void>();
    @Output() clickOnFile = new EventEmitter<FileModel>();
    @Output() selectMultipleFiles = new EventEmitter<FileModel[]>();
    @Output() openModalForTraining = new EventEmitter<Dataset>();

    /**
     * All files or folders
     */
    filesOrFolders: FileModel[] | DatasetFolder[] = [];

    /**
     * Filtered result (i.e. by search term). Subset of this.filesOrFolders
     */
    filteredFilesOrFolders: FileModel[] | DatasetFolder[] = [];
    /**
     * files from current page. Subset of this.filteredFilesOrFolders
     */
    pagedFilesOrFolders: FileModel[] | DatasetFolder[] = [];

    /**
     * Stores the opened folders in a history stack to be able to navigate back.
     * The root folder is NOT stored in the history.
     */
    history: DatasetFolder[] = [];

    isLoadingDatasets: boolean = false;

    sortOptions: OptionItem<SortOption>[] = [
        { label: 'Sort by date desc', value: '-date' },
        { label: 'Sort by date asc', value: 'date' },
        { label: 'Sort by A-Z', value: 'name' },
        { label: 'Sort by Z-A', value: '-name' },
    ];

    FILE_EXPLORER_MODE = FILE_EXPLORER_MODE;

    /**
     * Holds the folder we're currently in
     */
    get currentFolder(): DatasetFolder | undefined {
        return this.history[this.history.length - 1];
    }

    get workflowType(): typeof WorkflowType {
        return WorkflowType;
    }

    get datasetFileTypeOption(): typeof DatasetFileTypeOption {
        return DatasetFileTypeOption;
    }

    get retryMessagingAction(): MessagingCallbackAction<void> {
        return { topicId: this.messagingTopicId, action: CZI_CONVERSION_RETRY };
    }

    get datasetFiles(): DatasetFiles {
        return this._datasetFiles;
    }

    hasAnnotations = false;
    hasObjectAnnotations = false;
    hasImages = false;
    hasOver1kImages = false;
    currentDataset: Dataset;
    hasInvalidValidationStatus: number = 0;
    invalidFiles: FileModel[] = [];
    failedConversionFiles: FileModel[] = [];
    pendingFilesToValidate: FileModel[] = [];
    unknownFiles: FileModel[] = [];

    private arePendingFilesSetTheFirstTime = false;
    private apeerFilesMap: Map<string, FileModel>;
    private _datasetFiles: DatasetFiles;
    private currentSearchParams: ListSearch;
    private datasetFetched: boolean = false;
    private readonly availableFolders: DatasetFolder[] = [
        {
            name: 'Annotations',
            folderType: DatasetFileTypeOption.ANNOTATIONS,
            jsonPropName: 'annotations',
        },
        {
            name: 'Images',
            folderType: DatasetFileTypeOption.FILES,
            jsonPropName: 'files',
        },
    ];
    private currentFolderName: string | null = null;
    private messagingTopicId = Guid.create().toString();

    constructor(
        private activatedRoute: ActivatedRoute,
        private arivisService: ArivisService,
        private cziConverterService: CziConverterService,
        private cziValidatorService: CziValidatorService,
        private machineLearningService: MachineLearningService,
        private messagingService: MessagingService,
        private mixpanelTrackingService: TrackingService,
        private userService: UserService,
        private router: Router,
        private workflowRunQueueService: WorkflowRunQueueService,
    ) {
        this.messagingService
            .subscription(this.messagingTopicId)
            .pipe(
                tap((message: Message<FileModel | { files: FileModel[] }>): void => {
                    switch (message.action) {
                        case CZI_CONVERSION_DONE_NEXT_ADD_IMAGES_TO_DATASET:
                            this.addFilesToDataset.emit({
                                dataset: this.currentDataset,
                                datasetFileTypeOption: DatasetFileTypeOption.FILES,
                            });
                            break;
                        case CZI_CONVERSION_DONE_NEXT_ANNOTATE_DATASET:
                            this.annotateDataset.emit(this.currentDataset);
                            break;
                        case CZI_CONVERSION_DONE_NEXT_START_TRAINING_WITH_DATASET:
                            this.openModalForTraining.emit(this.currentDataset);
                            break;
                        case CZI_CONVERSION_REMOVED_FAILED_CONVERSION_FILE:
                            this.initFetchDataset();
                            break;
                        case CZI_CONVERSION_RETRY:
                            if (message.payload) {
                                const { files } = message.payload as { files: FileModel[] };
                                this.convertFilesToCzi(files);
                            }
                            break;
                        default:
                            break;
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    get isCziConversionInProgress(): boolean {
        return this.datasetFiles && this.cziConverterService.areSomeFilesActiveOrUnknown(this.datasetFiles.files);
    }

    get isValidationInProgress(): boolean {
        return this.datasetFiles && this.cziValidatorService.areSomeFilesPendingValidationOrUnknown(this.datasetFiles.files);
    }

    get numberOfConversionFailedFiles(): number {
        return this.datasetFiles && this.cziConverterService.getNumberOfConversionFailedFiles(this.datasetFiles.files);
    }

    ngOnInit(): void {
        this.activatedRoute.queryParamMap
            .pipe(
                tap((queryParamMap: ParamMap): void => {
                    const datasetId: string | null = queryParamMap.get('datasetId');
                    this.currentFolderName = queryParamMap.get('folder');
                    if (!this.datasetFetched) {
                        this.putInitialFolderIntoHistory(this.initializeInFolder);
                        this.initFetchDataset();
                    } else if (this.currentFolderName == null && this.currentFolder != null) {
                        if (datasetId) {
                            this.openRoot();
                        }
                    } else if (this.currentFolderName != null && this.currentFolder == null) {
                        this.updateFolderQueryParams();
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.cziConverterService.clearConversionMap(this.currentDataset.datasetId);
    }

    clickFile(file: FileModel): void {
        this.clickOnFile.emit(file);
    }

    addImagesToDataset(): void {
        this.cziConverterService
            .confirmCziConversionIfNeededAndTriggerCallbackWhenDone$(
                this.currentDataset.datasetId,
                { datasetId: this.currentDataset.datasetId, files: this.datasetFiles.files },
                { topicId: this.messagingTopicId, action: CZI_CONVERSION_DONE_NEXT_ADD_IMAGES_TO_DATASET },
            )
            .pipe(
                tap((): void => {
                    this.updateFiles();
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    numberOfInvalidFiles(dirOrFile: FileModel | DatasetFolder): number | null {
        // Check if dirOrFile is a DatasetFolder with folderType !== 'Files'
        if ('folderType' in dirOrFile && dirOrFile.folderType !== DatasetFileTypeOption.FILES) {
            return null;
        }

        const failedConversions = this.datasetFiles?.filesConversions?.filter(fc => fc.status !== 'Success') ?? [];
        return this.invalidFiles.length + failedConversions.length;
    }

    onTriggerSearch(searchParams: ListSearch): void {
        this.currentSearchParams = searchParams;
        this.prepareFilesForUi();
    }

    navigateBackToOverview(): void {
        this.leaveDataset.emit();
    }

    isFolder(fileOrFolder: FileModel | DatasetFolder): boolean {
        return (fileOrFolder as DatasetFolder).folderType != null;
    }

    openFolder(folderType: DatasetFileTypeOption, shouldConvertNotCziFiles: boolean = false): void {
        const targetFolder: DatasetFolder | undefined = this.getFolderByType(folderType);
        if (targetFolder != null) {
            this.filesOrFolders = this.datasetFiles[targetFolder.jsonPropName];
            this.prepareFilesForUi();
            if (folderType === DatasetFileTypeOption.FILES) {
                if (shouldConvertNotCziFiles) {
                    this.convertFilesToCzi(this.datasetFiles.files);
                } else {
                    this.updateFileStatuses();
                }
            }

            if (this.useRouting) {
                const newQueryParams: Params = { ...this.activatedRoute.snapshot.queryParams };
                newQueryParams.datasetId = this.currentDataset.datasetId;
                newQueryParams.folder = targetFolder.name;
                this.router.navigate([], {
                    queryParams: newQueryParams,
                    relativeTo: this.activatedRoute,
                });
            }
            // Add opened folder to history
            if (!this.currentFolder || (this.currentFolder != null && this.currentFolder.folderType !== folderType)) {
                this.history.push(targetFolder);
            }

            // Disable download option for .objects files.
            // Saving old values from fileActions array, while the current folder is Annotation folder.
            if (this.isCurrentFolderAnnotationsFolder() && this.datasetFiles.annotations.some(annotation => annotation.type == 'objects')) {
                this.fileActions = this.fileActions.filter((action: FileAction): boolean => action !== 'download');
            }
        }
    }

    openRoot(): void {
        this.filesOrFolders = this.availableFolders;
        this.filteredFilesOrFolders = this.filesOrFolders;
        this.history = [];
        if (this.useRouting) {
            const newQueryParams: Params = { ...this.activatedRoute.snapshot.queryParams };
            newQueryParams.datasetId = this.currentDataset.datasetId;

            if (newQueryParams.folder) {
                newQueryParams.folder = null;
            }
            this.router.navigate([], { queryParams: newQueryParams, relativeTo: this.activatedRoute, queryParamsHandling: 'merge' });
        }
    }

    getFolderByType(type: DatasetFileTypeOption): DatasetFolder | undefined {
        return this.availableFolders.find((folder: DatasetFolder): boolean => folder.folderType === type);
    }

    getNumberOfFilesOfFolder(folder: DatasetFolder): number {
        let num = 0;
        if (
            folder != null &&
            (folder.jsonPropName.toLowerCase() === DatasetFileTypeOption.FILES.toLowerCase() ||
                folder.jsonPropName.toLowerCase() === DatasetFileTypeOption.ANNOTATIONS.toLowerCase())
        ) {
            num = this.datasetFiles[folder.jsonPropName].length;
        }

        return num;
    }

    delete(removeFilesOptions: ItemsToBeRemovedOptions<FileModel>): void {
        this.removeFilesFromDataset.emit({
            datasetFileTypeOption: this.currentFolder?.folderType || DatasetFileTypeOption.FILES,
            items: [...removeFilesOptions.items],
            shouldConfirmDialogBeOpened: removeFilesOptions.shouldConfirmDialogBeOpened,
        });
    }

    isCurrentFolderFilesFolder(): boolean {
        return this.currentFolder?.folderType === DatasetFileTypeOption.FILES;
    }

    isCurrentFolderAnnotationsFolder(): boolean {
        return this.currentFolder?.folderType === DatasetFileTypeOption.ANNOTATIONS;
    }

    hasRunningTrainingWorkflow(): boolean {
        return this.workflowRunQueueService.hasRunningTrainingWorkflow();
    }

    annotate(): void {
        // Mixpanel tracking - for opening dataset (eg. annotation)
        const userId = this.userService.myInfo?.sub;

        this.mixpanelTrackingService.trackEvent('open_dataset', { userId, datasetId: this.currentDataset.datasetId });

        this.cziConverterService
            .confirmCziConversionIfNeededAndTriggerCallbackWhenDone$(
                this.currentDataset.datasetId,
                { datasetId: this.currentDataset.datasetId, files: this.datasetFiles.files },
                { topicId: this.messagingTopicId, action: CZI_CONVERSION_DONE_NEXT_ANNOTATE_DATASET },
                this.currentDataset.name,
            )
            .pipe(
                tap((): void => this.updateFiles()),
                untilDestroyed(this),
            )
            .subscribe();
    }

    onUseForTrainingButtonClick(): void {
        this.cziConverterService
            .confirmCziConversionIfNeededAndTriggerCallbackWhenDone$(this.currentDataset.datasetId, this.currentDataset, {
                topicId: this.messagingTopicId,
                action: CZI_CONVERSION_DONE_NEXT_START_TRAINING_WITH_DATASET,
                payload: this.currentDataset,
            })
            .pipe(untilDestroyed(this))
            .subscribe();
    }

    getTypedAsFileModelArray(filesOrFolders: DatasetFolder[] | FileModel[]): FileModel[] {
        return filesOrFolders as FileModel[];
    }

    reValidate(): void {
        const filesToValidate = [...this.pendingFilesToValidate, ...this.unknownFiles, ...this.invalidFiles];
        const updatedFiles = FileUtils.getUpdatedSelectedFiles(
            this.datasetFiles.files,
            filesToValidate.map(file => {
                file.validationStatus = 'PendingValidation';
                return file;
            }),
        );
        this.refreshDatasetFiles(Object.assign(this.datasetFiles, { files: updatedFiles }));
        this.cziValidatorService
            .validate$(this.currentDataset.datasetId, filesToValidate, true)
            .pipe(
                tap((): void => {
                    this.initFetchDataset();
                    this.pendingFilesToValidate = [];
                    this.unknownFiles = [];
                    this.invalidFiles = [];
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    private refreshDatasetFiles(datasetFiles: DatasetFiles): void {
        this._datasetFiles = datasetFiles;
        this.hasAnnotations = this.datasetFiles && this.datasetFiles.annotations.length > 0;
        this.hasObjectAnnotations = this.hasAnnotations && this.datasetFiles.annotations.some(annotation => annotation.type == 'objects');
        this.hasImages = this.datasetFiles && this.datasetFiles.files.length > 0;
        this.hasOver1kImages = this.datasetFiles && this.datasetFiles.files.length > 1000;

        this.isLoadingDatasets = false;
    }

    private prepareFilesForUi(): void {
        this.prepareFilteredAndPagedFiles();
    }

    private prepareFilteredAndPagedFiles(): void {
        this.filteredFilesOrFolders = getShownFilesBySearchAndSortOptions(this.filesOrFolders as FileModel[], this.currentSearchParams);
        this.filesOrFolders.forEach(f => {
            (f as FileModelRow).selected = false;
        });
        this.pagedFilesOrFolders = getCurrentPageOfFiles(this.filteredFilesOrFolders, this.currentSearchParams);
        this.pagedFilesOrFolders.forEach(f => {
            (f as FileModelRow).selected = false;
        });
        this.isLoadingDatasets = false;
    }

    private initFetchDataset(): void {
        this.isLoadingDatasets = true;
        this.machineLearningService
            .fetchDatasetContent$(this.currentDataset.datasetId)
            .pipe(
                map((res: DatasetFiles): DatasetFiles => {
                    const fileModelInstances: FileModel[] = res.files.map((file: FileModel): FileModel => {
                        const fileModelInstance: FileModel = FileModel.createInstance(file);
                        fileModelInstance.viewerUrl = this.arivisService.generateArivisMlViewerUrl(file);
                        return fileModelInstance;
                    });
                    res.files = fileModelInstances;
                    return res;
                }),
                tap((res: DatasetFiles): void => {
                    this.isLoadingDatasets = false;
                    this.datasetFetched = true;
                    this.datasetFiles = res;
                    this.updateFileStatuses(res.filesConversions);

                    this.updateFolderQueryParams();
                    this.initFilesAndFolders(this.currentFolder ? this.currentFolder.folderType : undefined);
                    this.onTriggerSearch(this.currentSearchParams);
                }),
            )
            .subscribe();
    }

    /**
     * If we initialize the dataset explorer in a certain folder, we need to put that folder into the history.
     * By that we're able to go back to root.
     */
    private putInitialFolderIntoHistory(initialFolderType?: DatasetFileTypeOption): void {
        if (initialFolderType != null) {
            const targetFolder: DatasetFolder | undefined = this.getFolderByType(initialFolderType);
            if (targetFolder != null) {
                this.history.push(targetFolder);
            }
        }
    }

    /**
     * Prepares the correct data to be shown.
     *
     * @param filesOrFolderTypesToShow
     *        can be:
     *           - DatasetFileTypeOption.ANNOTATIONS - Files from Annotations folder will be shown
     *           - DatasetFileTypeOption.FILES - Files from Files folder will be shown
     *           - undefined - top level will be shown (folders: Annotations and Files)
     */
    private initFilesAndFolders(filesOrFolderTypesToShow?: DatasetFileTypeOption): void {
        if (filesOrFolderTypesToShow != null) {
            this.openFolder(filesOrFolderTypesToShow);
        } else {
            this.openRoot();
        }
    }

    private updateFolderQueryParams(): void {
        if (this.currentFolderName && (this.currentFolderName === this.availableFolders[0].name || this.currentFolderName === this.availableFolders[1].name)) {
            const folderType: DatasetFileTypeOption =
                this.currentFolderName === this.availableFolders[0].name ? DatasetFileTypeOption.ANNOTATIONS : DatasetFileTypeOption.FILES;
            this.openFolder(folderType);
        }
    }

    private updateFileStatuses(fileConversionsStatuses?: FileConversionStatus[]): void {
        this.cziConverterService
            .updateOperationFileStatuses$(this.currentDataset.datasetId, this.datasetFiles.files, this.apeerFilesMap, fileConversionsStatuses)
            .pipe(
                tap((): void => {
                    this.updateFiles();
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    private convertFilesToCzi(files: FileModel[]): void {
        if (files.length > 0) {
            for (let i = 0; i <= files.length - 2; i++) {
                this.cziConverterService
                    .convertFileToCziIfNotCziAndStartPolling$(
                        this.currentDataset.datasetId,
                        this.datasetFiles.files,
                        files[i],
                        this.currentDataset.datasetId,
                        this.apeerFilesMap,
                    )
                    .pipe(untilDestroyed(this))
                    .subscribe();
            }
            this.cziConverterService
                .convertFileToCziIfNotCziAndStartPolling$(
                    this.currentDataset.datasetId,
                    this.datasetFiles.files,
                    files[files.length - 1],
                    this.currentDataset.datasetId,
                    this.apeerFilesMap,
                )
                .pipe(
                    tap((): void => {
                        this.updateFiles();
                    }),
                    untilDestroyed(this),
                )
                .subscribe();
            this.updateFiles();
        }
    }

    // this is not good way the whole thing
    private updateFiles(): void {
        this.currentDataset.statistics.numberOfAnnotationFiles = this.datasetFiles.annotations.length;
        this.currentDataset.statistics.numberOfCziFiles = FileUtils.getNumberOfCziFiles(this.datasetFiles.files);
        this.currentDataset.statistics.numberOfFiles = this.datasetFiles.files.length;
        this.currentDataset.statistics.numberOfInvalidFiles = FileUtils.getNumberOfInvalidDatasetFiles(this.datasetFiles.files);
        this.unknownFiles = this.datasetFiles
            ? this.datasetFiles.files.filter(file => file.validationStatus && FileUtils.hasUnknownValidationStatus(file.validationStatus))
            : [];
        this.prepareFilteredAndPagedFiles();
    }
}
